/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { CookieService } from 'ngx-cookie-service';
import { PrimeNGConfig } from 'primeng/api';
import { Observable, mergeMap, pipe } from 'rxjs';
import { AVAILABLE_LOCALES, FALLBACK_LANG, Locale } from 'scoped-translations';
import { LocalizationService } from './localization.service';

interface LocalizationState {
  locale: Locale;
}

const initialState: LocalizationState = {
  locale: FALLBACK_LANG,
};

const FIRST_DAY_OF_WEEK: Record<Locale, number> = {
  // 0 = sunday, 1 = monday, ...
  'de-DE': 1,
  'en-GB': 1,
  'es-ES': 1,
  'fr-FR': 1,
  'it-IT': 1,
  'nl-BE': 1,
};

const LOCALE_COOKIE_NAME = 'org.springframework.web.servlet.i18n.CookieLocaleResolver.LOCALE';

@Injectable({ providedIn: 'root' })
export class LocalizationStore extends ComponentStore<LocalizationState> {
  constructor(
    private localizationService: LocalizationService,
    private translateService: TranslocoService,
    private cookieService: CookieService,
    private primeNgConfig: PrimeNGConfig
  ) {
    super(initialState);
  }

  readonly locale$: Observable<Locale> = this.select((state) => state.locale);

  readonly setBackendLocale = this.effect<Locale>(
    pipe(mergeMap((locale) => this.localizationService.setBackendLocale(locale)))
  );

  private setPrimeNgConfig = this.effect<Locale>(
    pipe(
      mergeMap((locale) =>
        this.translateService.selectTranslateObject('calendar').pipe(
          tapResponse(
            (result) => this.updatePrimeNg(locale, result),
            (error) => console.error('failed to set primeNg translation', error)
          )
        )
      )
    )
  );

  setLocale(locale: Locale) {
    this.translateService.setActiveLang(locale);
    this.setPrimeNgConfig(locale);
    this.setBackendLocale(locale);
    this.patchState({
      locale: locale,
    });
  }

  private updatePrimeNg(locale: Locale, result: any) {
    this.primeNgConfig.setTranslation({
      firstDayOfWeek: FIRST_DAY_OF_WEEK[locale],
      dayNames: [
        result.days.full.sun,
        result.days.full.mon,
        result.days.full.tue,
        result.days.full.wed,
        result.days.full.thu,
        result.days.full.fri,
        result.days.full.sat,
      ],
      dayNamesShort: [
        result.days.short.sun,
        result.days.short.mon,
        result.days.short.tue,
        result.days.short.wed,
        result.days.short.thu,
        result.days.short.fri,
        result.days.short.sat,
      ],
      dayNamesMin: [
        result.days.veryShort.sun,
        result.days.veryShort.mon,
        result.days.veryShort.tue,
        result.days.veryShort.wed,
        result.days.veryShort.thu,
        result.days.veryShort.fri,
        result.days.veryShort.sat,
      ],
      monthNames: [
        result.months.full.jan,
        result.months.full.feb,
        result.months.full.mar,
        result.months.full.apr,
        result.months.full.may,
        result.months.full.jun,
        result.months.full.jul,
        result.months.full.aug,
        result.months.full.sep,
        result.months.full.oct,
        result.months.full.nov,
        result.months.full.dec,
      ],
      monthNamesShort: [
        result.months.short.jan,
        result.months.short.feb,
        result.months.short.mar,
        result.months.short.apr,
        result.months.short.may,
        result.months.short.jun,
        result.months.short.jul,
        result.months.short.aug,
        result.months.short.sep,
        result.months.short.oct,
        result.months.short.nov,
        result.months.short.dec,
      ],
    });
  }

  init() {
    this.loadLocale();
  }

  private loadLocale() {
    const cookieLocale = this.cookieService.get(LOCALE_COOKIE_NAME) as Locale;
    let locale: Locale;
    if (!AVAILABLE_LOCALES.includes(cookieLocale)) {
      const browserLocale = AVAILABLE_LOCALES.find((l) => l === navigator.language || l.includes(navigator.language));
      locale = browserLocale ? browserLocale : FALLBACK_LANG;
    } else {
      locale = cookieLocale;
    }
    this.setLocale(locale);
  }
}
